import React from 'react';
import { useProgressionStore } from '../store/progressionStore';
import { useGameStore } from '../store/gameStore';
import { useAdminStore } from '../store/adminStore';
import { PRIMITIVE_ORDER, PRIMITIVE_DEFINITIONS, PrimitiveType } from '../types/progression';
import { Lock, CheckCircle, Play, Star, Shield, Zap, Activity } from 'lucide-react';

const PRIMITIVE_SCENARIOS: Record<PrimitiveType, string> = {
  'OPSEC': 'tutorial-opsec',
  'PublicAffairs': 'tutorial-pa',
  'Intel': 'tutorial-intel',
  'EW': 'tutorial-ew',
  'TechnicalActivities': 'tutorial-tech',
  'DeceptiveActivities': 'tutorial-decoy',
  'PsyOp': 'tutorial-psyop'
};

export const PrimitivesSelector: React.FC = () => {
  const { 
    unlockedPrimitives, 
    primitiveScores, 
    setCurrentPrimitive,
    isLevel2Unlocked,
    isLevel3Unlocked,
    level2Completions
  } = useProgressionStore();
  
  const { startGame } = useGameStore();
  const { toggleDashboard } = useAdminStore();
  const [toast, setToast] = React.useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const [confirmTrain, setConfirmTrain] = React.useState<PrimitiveType | null>(null);

  const handleTrain = (primitive: PrimitiveType) => {
    // Require confirmation before closing admin panel and launching game
    setConfirmTrain(primitive);
  };

  const confirmAndTrain = () => {
    if (!confirmTrain) return;
    const primitive = confirmTrain;
    setConfirmTrain(null);
    setCurrentPrimitive(primitive);
    const scenarioId = PRIMITIVE_SCENARIOS[primitive];
    if (scenarioId) {
      startGame(scenarioId);
      toggleDashboard();
      showToast(`Starting ${PRIMITIVE_DEFINITIONS[primitive].name} training...`, 'success');
    } else {
      showToast("Scenario not found.", "error");
    }
  };

  const handleLevel2 = () => {
    setCurrentPrimitive(null); // Not a primitive
    startGame('combined-arms-1'); // Need to ensure this scenario exists or is handled
    toggleDashboard();
    showToast("Starting Combined Arms Operation...", "success");
  };

  const handleLevel3 = () => {
    setCurrentPrimitive(null);
    startGame('full-spectrum-1');
    toggleDashboard();
    showToast("Starting Full Spectrum Operation...", "success");
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 max-w-4xl mx-auto space-y-8 relative">
      {toast && (
        <div className={`absolute top-4 right-4 z-50 px-4 py-2 rounded shadow-lg flex items-center gap-2 animate-fade-in ${
          toast.type === 'success' ? 'bg-green-500/90 text-white' : 'bg-red-500/90 text-white'
        }`}>
          {toast.type === 'success' ? <CheckCircle size={16} /> : <Lock size={16} />}
          {toast.message}
        </div>
      )}

      {/* Confirmation dialog for Retrain / Train */}
      {confirmTrain && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm">
          <div className="bg-slate-900 border border-ops-blue/40 rounded-xl p-8 max-w-sm w-full shadow-2xl">
            <h3 className="text-lg font-bold text-white mb-2">Launch Training?</h3>
            <p className="text-slate-400 text-sm mb-6">
              This will close the Admin panel and start the{' '}
              <span className="text-ops-blue font-bold">{PRIMITIVE_DEFINITIONS[confirmTrain].name}</span>{' '}
              training scenario.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmTrain(null)}
                className="px-4 py-2 rounded border border-slate-700 text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmAndTrain}
                className="px-4 py-2 rounded bg-ops-blue hover:bg-blue-600 text-white font-bold transition-colors flex items-center gap-2"
              >
                <Play className="w-4 h-4" /> Start Training
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Level 1: Primitives */}
      <div>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
            <Shield className="text-ops-blue" />
            Level 1: Primitives
          </h2>
          <p className="text-slate-400">
            Master each capability individually to unlock advanced combined-arms operations.
            Score 85% or higher to progress.
          </p>
        </div>

        <div className="grid gap-4">
          {PRIMITIVE_ORDER.map((primitiveId, index) => {
            const def = PRIMITIVE_DEFINITIONS[primitiveId];
            const isUnlocked = unlockedPrimitives.includes(primitiveId);
            const score = primitiveScores[primitiveId];
            const isMastered = score >= def.requiredScore;
            
            return (
              <div 
                key={primitiveId}
                className={`relative flex items-center justify-between p-4 rounded-lg border transition-all ${
                  isUnlocked 
                    ? 'bg-slate-800 border-slate-700 hover:border-ops-blue/50' 
                    : 'bg-slate-900/50 border-slate-800 opacity-60'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                    isMastered ? 'bg-green-500/20 text-green-400' :
                    isUnlocked ? 'bg-ops-blue/20 text-ops-blue' :
                    'bg-slate-800 text-slate-600'
                  }`}>
                    {isMastered ? <CheckCircle className="w-6 h-6" /> : index + 1}
                  </div>
                  
                  <div>
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      {def.name}
                      {!isUnlocked && <Lock className="w-4 h-4 text-slate-500" />}
                    </h3>
                    <p className="text-sm text-slate-400">{def.description}</p>
                  </div>
                </div>

                <div className="flex items-center gap-6">
                  {isUnlocked && (
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-white font-mono font-bold">
                        <Star className={`w-4 h-4 ${isMastered ? 'text-yellow-400 fill-yellow-400' : 'text-slate-600'}`} />
                        {score}%
                      </div>
                    </div>
                  )}

                  <button
                    onClick={() => isUnlocked ? handleTrain(primitiveId) : showToast("Locked: Complete previous primitives first.", "error")}
                    className={`px-4 py-2 rounded flex items-center gap-2 font-bold transition-colors ${
                      isUnlocked
                        ? 'bg-ops-blue hover:bg-blue-600 text-white'
                        : 'bg-slate-800 text-slate-500 cursor-not-allowed'
                    }`}
                  >
                    <Play className="w-4 h-4" />
                    {isMastered ? 'Retrain' : 'Train'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Level 2: Combined Arms */}
      <div className={`border-t border-slate-800 pt-8 ${!isLevel2Unlocked ? 'opacity-50 grayscale' : ''}`}>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
            <Activity className={isLevel2Unlocked ? "text-purple-400" : "text-slate-600"} />
            Level 2: Combined Arms
            {!isLevel2Unlocked && <Lock className="w-5 h-5 text-slate-500 ml-2" />}
          </h2>
          <p className="text-slate-400">
            Execute missions requiring multiple synchronized capabilities.
            {isLevel2Unlocked 
              ? `Completed: ${level2Completions}/3 missions.` 
              : "Complete all Level 1 Primitives to unlock."}
          </p>
        </div>

        <div className="bg-slate-800 p-6 rounded-lg border border-slate-700 flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-white">Operation: Silent Echo</h3>
            <p className="text-slate-400">Coordinate EW and Cyber to disable enemy comms while preserving OPSEC.</p>
            <div className="flex gap-2 mt-2">
              <span className="px-2 py-1 bg-slate-900 rounded text-xs text-purple-400 border border-slate-700">EW</span>
              <span className="px-2 py-1 bg-slate-900 rounded text-xs text-purple-400 border border-slate-700">Cyber</span>
              <span className="px-2 py-1 bg-slate-900 rounded text-xs text-purple-400 border border-slate-700">OPSEC</span>
            </div>
          </div>
          <button
            onClick={() => isLevel2Unlocked ? handleLevel2() : showToast("Locked: Complete all Level 1 Primitives.", "error")}
            className={`px-6 py-3 rounded-lg flex items-center gap-2 font-bold transition-colors ${
              isLevel2Unlocked
                ? 'bg-purple-600 hover:bg-purple-500 text-white'
                : 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700'
            }`}
          >
            <Play className="w-5 h-5" />
            Start Mission
          </button>
        </div>
      </div>

      {/* Level 3: Full Spectrum */}
      <div className={`border-t border-slate-800 pt-8 ${!isLevel3Unlocked ? 'opacity-50 grayscale' : ''}`}>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
            <Zap className={isLevel3Unlocked ? "text-orange-400" : "text-slate-600"} />
            Level 3: Full Spectrum
            {!isLevel3Unlocked && <Lock className="w-5 h-5 text-slate-500 ml-2" />}
          </h2>
          <p className="text-slate-400">
            High-intensity operations with all domains enabled and active adversaries.
            {!isLevel3Unlocked && " Complete 3 Level 2 missions to unlock."}
          </p>
        </div>

        <div className="bg-slate-800 p-6 rounded-lg border border-slate-700 flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-white">Operation: Total Dominance</h3>
            <p className="text-slate-400">Full-scale information warfare campaign across all sectors.</p>
            <div className="flex gap-2 mt-2">
              <span className="px-2 py-1 bg-slate-900 rounded text-xs text-orange-400 border border-slate-700">All Domains</span>
              <span className="px-2 py-1 bg-slate-900 rounded text-xs text-red-400 border border-slate-700">Active Red Team</span>
            </div>
          </div>
          <button
            onClick={() => isLevel3Unlocked ? handleLevel3() : showToast("Locked: Complete 3 Level 2 missions.", "error")}
            className={`px-6 py-3 rounded-lg flex items-center gap-2 font-bold transition-colors ${
              isLevel3Unlocked
                ? 'bg-orange-600 hover:bg-orange-500 text-white'
                : 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700'
            }`}
          >
            <Play className="w-5 h-5" />
            Start Campaign
          </button>
        </div>
      </div>

    </div>
  );
};
